// 举手（请求支援）演示数据与本地状态：**不连后端**，只为评审看界面与动作。
// 真实实现时这一层整体替换为 /support/requests 接口与轮询；UI 组件不用改结构。

import { create } from 'zustand';

export type SupportLevel = 'agent' | 'leader' | 'duty';
/**
 * 报告送给谁：组长 / 值班长 / 技术值班。坐席发起时三选一；组长发起固定送值班长。
 * 「技术」在本仓没有独立角色与收件箱：它归到值班长的「技术值班」名下处理
 * （见 SupportRequestDrawer 的 LEVEL_TITLE.duty），所以接收角色仍是 duty。
 */
export type SupportTarget = 'leader' | 'duty' | 'tech';
export type SupportStatus = 'pending' | 'accepted' | 'resolved' | 'escalated';
/** 报告接收角色：坐席上送组长或值班长，组长上送值班长，值班长上送管理员（不在两张现场卡里出现）。 */
export type SupportReceiver = 'leader' | 'duty' | 'admin';

export interface SupportReply {
  by: string;
  text: string;
  at: number;
}

export interface MockSupportItem {
  id: string;
  /** 发起层级：agent=坐席→组长/班长；leader=组长→值班长；duty=值班长→管理员 */
  level: SupportLevel;
  fromName: string;
  fromExtra?: string;
  /** 只对 level=agent 有意义：发给组长还是班长（缺省按组长，兼容旧种子） */
  target?: SupportTarget;
  typeLabel: string;
  subject: string;
  expect: string[];
  /** 演示用：初始已等待秒数；页面上会随时间继续走 */
  waitingSec: number;
  customerWaiting?: boolean;
  status: SupportStatus;
  assignee?: string;
  result?: string;
  replies: SupportReply[];
  context?: string[];
}

export interface NewSupportInput {
  level: SupportLevel;
  fromName: string;
  fromExtra?: string;
  target?: SupportTarget;
  typeLabel: string;
  subject: string;
  expect: string[];
  customerWaiting?: boolean;
  context?: string[];
}

interface SupportMockState {
  items: MockSupportItem[];
  add: (input: NewSupportInput) => void;
  ack: (id: string, by: string) => void;
  reply: (id: string, by: string, text: string) => void;
  resolve: (id: string, result: string) => void;
  escalate: (id: string) => void;
  cancel: (id: string) => void;
}

let seq = 0;
const nextId = () => `s-${Date.now()}-${++seq}`;

/** 演示初始数据：组长收坐席发给组长的、值班长收坐席发给班长的 + 组长发给值班长的 */
const SEED: MockSupportItem[] = [
  {
    id: 'demo-agent-1',
    level: 'agent',
    fromName: '坐席1005',
    fromExtra: '分机 1005 · 客服支持',
    target: 'leader',
    typeLabel: '要授权',
    subject: '客户要求退 30 元，需要授权',
    expect: ['授权'],
    waitingSec: 42,
    customerWaiting: true,
    status: 'pending',
    replies: [],
    context: ['通话 138****1234 · 已通话 06:12', '客户情绪：不满'],
  },
  {
    id: 'demo-agent-2',
    level: 'agent',
    fromName: '坐席1002',
    fromExtra: '分机 1002 · 客服支持',
    target: 'leader',
    typeLabel: '电话异常',
    subject: '听不到客户声音，换线后仍单通',
    expect: ['技术介入'],
    waitingSec: 128,
    customerWaiting: false,
    status: 'pending',
    replies: [],
    context: ['通话 139****8866 · 已通话 01:40', '已尝试：重拨、换分机'],
  },
  {
    id: 'demo-agent-3',
    level: 'agent',
    fromName: '坐席1008',
    fromExtra: '分机 1008 · 投诉专席',
    target: 'duty',
    typeLabel: '客户情绪升级',
    subject: '客户在电话里持续辱骂，需要班长介入',
    expect: ['你来接', '要口径'],
    waitingSec: 96,
    customerWaiting: true,
    status: 'pending',
    replies: [],
    context: ['通话 137****2211 · 已通话 03:20', '客户情绪：愤怒'],
  },
  {
    id: 'demo-leader-1',
    level: 'leader',
    fromName: '客服支持 · 张三',
    fromExtra: '组长',
    target: 'duty',
    typeLabel: '要人',
    subject: '本组排队 8 通，缺口约 2 人，请从 B 组借人',
    expect: ['从 B 组调人'],
    waitingSec: 70,
    customerWaiting: true,
    status: 'pending',
    replies: [],
    context: ['排队 8 通 · 最长等待 1 分 36 秒 · 在岗 3 · 示闲 1', '即将下班：1 人（17:30）'],
  },
  {
    id: 'demo-leader-2',
    level: 'leader',
    fromName: '宽带专席 · 李四',
    fromExtra: '组长',
    target: 'duty',
    typeLabel: '要决策',
    subject: '两通投诉同时升级，是否需要值班长回电',
    expect: ['值班长回电'],
    waitingSec: 240,
    customerWaiting: false,
    status: 'accepted',
    assignee: '值班长（示例）',
    replies: [{ by: '值班长（示例）', text: '收到，我 5 分钟内回电，你先安抚。', at: Date.now() - 60_000 }],
    context: ['涉及客户：138****0001 / 137****0002', '已给方案：改套餐 + 赠 10 元券'],
  },
];

export const useSupportMock = create<SupportMockState>((set) => ({
  items: SEED,
  add: (input) =>
    set((s) => ({
      items: [
        {
          id: nextId(),
          ...input,
          waitingSec: 0,
          status: 'pending',
          replies: [],
        },
        ...s.items,
      ],
    })),
  ack: (id, by) =>
    set((s) => ({
      items: s.items.map((i) => (i.id === id ? { ...i, status: 'accepted', assignee: by } : i)),
    })),
  reply: (id, by, text) =>
    set((s) => ({
      items: s.items.map((i) =>
        i.id === id ? { ...i, replies: [...i.replies, { by, text, at: Date.now() }] } : i,
      ),
    })),
  resolve: (id, result) =>
    set((s) => ({
      items: s.items.map((i) => (i.id === id ? { ...i, status: 'resolved', result } : i)),
    })),
  escalate: (id) =>
    set((s) => ({
      items: s.items.map((i) => (i.id === id ? { ...i, status: 'escalated' } : i)),
    })),
  cancel: (id) =>
    set((s) => ({
      items: s.items.map((i) => (i.id === id ? { ...i, status: 'resolved', result: '已撤回' } : i)),
    })),
}));

/**
 * 一条报告归谁处理：坐席按所选目标（缺省组长）、组长固定班长、值班长上报管理员。
 * 「技术」选项落值班长的技术值班（本仓没有技术角色与独立收件箱），所以只区分 leader / duty / admin。
 * 组长/值班长收件箱与右侧常驻卡共用这一口径，避免两处各写一遍过滤条件。
 */
export function supportReceiverOf(item: MockSupportItem): SupportReceiver {
  if (item.level === 'agent') return item.target === 'leader' ? 'leader' : 'duty';
  if (item.level === 'leader') return 'duty';
  return 'admin';
}

/** 某个接收角色应该看到的报告（含已解决的历史行；过滤未闭环请自行按 status 再筛） */
export function supportForReceiver(items: MockSupportItem[], role: 'leader' | 'duty'): MockSupportItem[] {
  return items.filter((i) => supportReceiverOf(i) === role);
}

// ---------- 表单选项（按层级；评审可改） ----------

export const SUPPORT_TYPES: Record<SupportLevel, string[]> = {
  agent: ['业务咨询', '要授权', '客户情绪升级', '电话异常', '要代接/转接', '合规卡住', '要二线专家'],
  leader: ['要人', '要授权', '要决策', '客户升级', '技术异常', '跨组协调'],
  duty: ['全场容量', '线路/系统异常', '重大投诉/舆情', '跨部门协调', '要授权'],
};

export const SUPPORT_EXPECTS: Record<SupportLevel, string[]> = {
  agent: ['给我答案', '授权', '你来接', '派人', '技术介入', '要口径'],
  leader: ['从别组调人', '暂停队列', '开溢出', '值班长回电', '要决策口径', '上级出面'],
  duty: ['技术介入', '审批授权', '跨部门协调', '管理员回电'],
};

/** 已经处理完可回填的结果口径（各层级通用） */
export const SUPPORT_RESULTS = ['给答案', '已授权', '已接管', '已派人', '已排障', '已调人', '已暂停队列', '已开溢出', '已转技术', '已升级'];
